import React, { Component } from 'react';
import { Button } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { API_URL } from '../../../settings/local_settings';
import { APIClient } from '../../../utils/APIClient';
import { Spinner } from '../../Spinner';
import { PLANS, PERIODS, getPrice, formatPrice } from '../../upgrade_to_dashbo/pricingConfig';

const IVA_RATE = 0.21;

const PERIOD_MAP_API = {
    monthly: 'mensual',
    trimestral: 'trimestral',
    semestral: 'semestral',
    yearly: 'anual'
};

const TAX_ID_LABELS = {
    'Argentina': 'CUIT',
    'Chile': 'RUT',
    'Colombia': 'NIT',
    'México': 'RFC',
    'Perú': 'RUC',
    'Uruguay': 'RUT',
    'Ecuador': 'RUC',
    'Paraguay': 'RUC',
    'Bolivia': 'NIT',
    'Costa Rica': 'NITE',
    'Panamá': 'RUC',
    'República Dominicana': 'RNC',
    'Guatemala': 'NIT',
    'El Salvador': 'NIT',
    'Honduras': 'RTN',
};

const TAX_CONDITIONS_AR = [
    { value: 'responsable_inscripto', labelKey: 'checkout.tax_condition_ri' },
    { value: 'monotributista', labelKey: 'checkout.tax_condition_mono' },
];

const COUNTRIES = [
    'Argentina', 'Chile', 'Colombia', 'México', 'Perú', 'Uruguay', 'Ecuador',
    'Paraguay', 'Bolivia', 'Costa Rica', 'Panamá', 'República Dominicana',
    'Guatemala', 'El Salvador', 'Honduras', 'España', 'Estados Unidos', 'Brasil'
];

const PROVINCIAS_AR = [
    'CABA', 'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Cordoba',
    'Corrientes', 'Entre Rios', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja',
    'Mendoza', 'Misiones', 'Neuquen', 'Rio Negro', 'Salta', 'San Juan',
    'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero',
    'Tierra del Fuego', 'Tucuman'
];

const PERIOD_LABEL_KEYS = {
    monthly: 'agency_subscription.period_monthly',
    trimestral: 'agency_subscription.period_trimestral',
    semestral: 'agency_subscription.period_semestral',
    yearly: 'agency_subscription.period_yearly'
};

const PERIOD_MONTHS = {
    monthly: 1,
    trimestral: 3,
    semestral: 6,
    yearly: 12
};

class CheckoutForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Billing form
            billingCountry: '',
            billingEmail: '',
            businessName: '',
            address: '',
            zipCode: '',
            city: '',
            state: '',
            taxId: '',
            taxCondition: '',
            paymentMethod: '',
            // Comment
            comment: '',
            showComment: false,
            // UI state
            sending: false,
            error: null,
            showSummary: false,
            showValidation: false,
        };
    }

    getPlan() {
        const { checkoutData } = this.props;
        return PLANS.find(p => p.id === checkoutData.plan.id);
    }

    isArgentina() {
        return this.state.billingCountry === 'Argentina';
    }

    getPriceDetails() {
        const { checkoutData } = this.props;
        const { paymentMethod } = this.state;
        const plan = this.getPlan();
        if (!plan) return null;

        const isArs = paymentMethod === 'mercadopago_ar'
            || (paymentMethod === 'bank_transfer' && this.isArgentina());
        const currency = isArs ? 'ars' : 'usd';
        const period = checkoutData.period;

        // Looker Studio va incluido en el plan (pricing del 2026-10-05): el subtotal es el precio del plan.
        const planPrice = getPrice(plan, currency, period);
        const subtotal = planPrice;

        const iva = isArs ? Math.round(subtotal * IVA_RATE) : 0;
        const total = subtotal + iva;

        // Discount info for non-monthly periods
        const discount = PERIODS[period]?.discount || 0;
        const periodMonths = PERIOD_MONTHS[period];
        const monthlySubtotal = getPrice(plan, currency, 'monthly');
        const monthlyIva = isArs ? Math.round(monthlySubtotal * IVA_RATE) : 0;
        const monthlyTotal = monthlySubtotal + monthlyIva;
        const periodTotal = total * periodMonths;
        const savings = monthlyTotal - total;

        return {
            currency: isArs ? 'ARS' : 'USD',
            currencySymbol: isArs ? 'ARS' : 'US$',
            planPrice,
            subtotal,
            iva,
            total,
            isArs,
            discount,
            periodMonths,
            monthlyTotal,
            periodTotal,
            savings
        };
    }

    isFormValid() {
        const { billingCountry, billingEmail, businessName, taxId, paymentMethod, taxCondition } = this.state;
        const base = billingCountry && billingEmail && businessName && taxId && paymentMethod;
        if (billingCountry === 'Argentina') {
            return base && taxCondition && taxCondition !== 'no_aplica';
        }
        return base;
    }

    handleSubmit() {
        const { t, checkoutData, onCheckoutComplete } = this.props;
        const plan = this.getPlan();
        if (!plan) return;

        this.setState({ sending: true, error: null });

        const payload = {
            plan_tier: plan.id,
            billing_period: PERIOD_MAP_API[checkoutData.period],
            billing_country: this.state.billingCountry,
            billing_email: this.state.billingEmail,
            business_name: this.state.businessName,
            address: this.state.address,
            zip_code: this.state.zipCode,
            city: this.state.city,
            state: this.state.state,
            tax_id: this.state.taxId,
            tax_condition: this.isArgentina() ? this.state.taxCondition : 'no_aplica',
            payment_method: this.state.paymentMethod,
            comment: this.state.comment,
        };

        new APIClient(API_URL + 'agency/subscription/checkout/')
            .call('POST', payload)
            .then((data) => {
                if (data.status === 'OK') {
                    this.setState({ sending: false });
                    if (onCheckoutComplete) onCheckoutComplete(data.subscription_url, data.has_comment, data.bank_transfer || null);
                } else {
                    this.setState({ sending: false, error: data.error_message || 'Error' });
                }
            })
            .catch(() => {
                this.setState({ sending: false, error: t('checkout.connection_error') });
            });
    }

    getEmptyRequiredFields() {
        const { billingCountry, billingEmail, businessName, taxId, paymentMethod, taxCondition } = this.state;
        const empty = [];
        if (!billingCountry) empty.push('billingCountry');
        if (!billingEmail) empty.push('billingEmail');
        if (!businessName) empty.push('businessName');
        if (!taxId) empty.push('taxId');
        if (!paymentMethod) empty.push('paymentMethod');
        if (billingCountry === 'Argentina' && (!taxCondition || taxCondition === 'no_aplica')) {
            empty.push('taxCondition');
        }
        return empty;
    }

    isFieldInvalid(name) {
        return this.state.showValidation && this.getEmptyRequiredFields().includes(name);
    }

    handleConfirmClick() {
        if (!this.isFormValid()) {
            this.setState({ showValidation: true });
            return;
        }
        this.handleSubmit();
    }

    renderField(label, name, type = 'text', required = true) {
        const invalid = required && this.isFieldInvalid(name);
        return (
            <div className="form-row" style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                    {label} {required && <span style={{ color: '#DF0D00' }}>*</span>}
                </label>
                <input
                    type={type}
                    className="form-control"
                    value={this.state[name]}
                    onChange={(e) => this.setState({ [name]: e.target.value })}
                    required={required}
                    style={invalid ? { borderColor: '#DF0D00', boxShadow: '0 0 0 2px rgba(223,13,0,0.15)' } : undefined}
                />
            </div>
        );
    }

    renderPaymentOption(value, title, description, fullWidth = false) {
        const selected = this.state.paymentMethod === value;
        return (
            <label style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0.85rem 1rem',
                border: selected ? '2px solid #FF470F' : '1px solid rgba(0,0,0,0.1)',
                borderRadius: '0.4rem',
                cursor: 'pointer',
                backgroundColor: selected ? 'rgba(255,71,15,0.03)' : '#fff',
                transition: 'border-color 0.15s, background 0.15s',
                position: 'relative',
                gridColumn: fullWidth ? '1 / -1' : undefined,
            }}>
                <input
                    type="radio"
                    name="payment"
                    checked={selected}
                    onChange={() => this.setState({ paymentMethod: value })}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.3em' }}>
                    <span style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        border: selected ? '4px solid #FF470F' : '2px solid #B6B6B7',
                        display: 'inline-block',
                        flexShrink: 0,
                        boxSizing: 'border-box'
                    }} />
                    <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{title}</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#B6B6B7', lineHeight: 1.4, paddingLeft: '1.6em' }}>
                    {description}
                </div>
            </label>
        );
    }

    render() {
        const { t, checkoutData, onBack } = this.props;
        const { sending, error, billingCountry, paymentMethod } = this.state;
        const isAnnual = checkoutData.period === 'yearly';
        const plan = this.getPlan();
        if (!plan) return null;

        const priceDetails = paymentMethod ? this.getPriceDetails() : null;

        return (
            <div className="box" style={{ marginBottom: '1.5rem' }}>
                <header className="box-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em' }}>
                        <h2 className="box-title">{t('checkout.title')}</h2>
                    </div>
                </header>

                <div style={{ padding: '1rem 1.5rem' }}>
                    {/* Resumen del plan elegido */}
                    <div style={{
                        backgroundColor: '#f0f6ff',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        marginBottom: '1.25rem',
                        border: '1px solid #d0e2ff'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'wrap' }}>
                                    <strong style={{ fontSize: '1.1rem' }}>{t(plan.nameKey)}</strong>
                                    <span style={{
                                        backgroundColor: '#259D92',
                                        color: '#fff',
                                        padding: '0.12em 0.5em',
                                        borderRadius: '0.25em',
                                        fontSize: '0.72em',
                                        fontWeight: 600
                                    }}>
                                        {t('checkout.all_features_badge')}
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.25em' }}>
                                    {t(PERIOD_LABEL_KEYS[checkoutData.period])}
                                </div>
                            </div>
                            <button
                                className="btn -alt -negative"
                                style={{ fontSize: '0.8em', padding: '0.35em 0.8em' }}
                                onClick={onBack}
                            >
                                {t('checkout.change_plan')}
                            </button>
                        </div>
                    </div>

                    {/* Formulario de facturación */}
                    <h6 style={{ fontWeight: 700, marginBottom: '1rem' }}>{t('checkout.billing_info')}</h6>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>
                        {/* País */}
                        <div className="form-row" style={{ marginBottom: '0.75rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                                {t('checkout.country')} <span style={{ color: '#DF0D00' }}>*</span>
                            </label>
                            <select
                                className="form-control"
                                value={billingCountry}
                                onChange={(e) => {
                                    const country = e.target.value;
                                    const pm = country === 'Argentina' ? '' : 'paypal';
                                    this.setState({ billingCountry: country, paymentMethod: pm, state: '', taxCondition: '' });
                                }}
                                style={this.isFieldInvalid('billingCountry') ? { borderColor: '#DF0D00', boxShadow: '0 0 0 2px rgba(223,13,0,0.15)' } : undefined}
                            >
                                <option value="">{t('checkout.select_country')}</option>
                                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        {/* Email */}
                        {this.renderField(t('checkout.billing_email'), 'billingEmail', 'email')}

                        {/* Razón social */}
                        {this.renderField(t('checkout.business_name'), 'businessName')}

                        {/* Identificador fiscal */}
                        {this.renderField(
                            TAX_ID_LABELS[billingCountry] || t('checkout.tax_id'),
                            'taxId'
                        )}

                        {/* Dirección */}
                        {this.renderField(t('checkout.address'), 'address', 'text', false)}

                        {/* Ciudad */}
                        {this.renderField(t('checkout.city'), 'city', 'text', false)}

                        {/* Provincia */}
                        {this.isArgentina() ? (
                            <div className="form-row" style={{ marginBottom: '0.75rem' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                                    {t('checkout.state')}
                                </label>
                                <select
                                    className="form-control"
                                    value={this.state.state}
                                    onChange={(e) => this.setState({ state: e.target.value })}
                                >
                                    <option value="">{t('checkout.select_state')}</option>
                                    {PROVINCIAS_AR.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        ) : (
                            this.renderField(t('checkout.state'), 'state', 'text', false)
                        )}

                        {/* Código postal */}
                        {this.renderField(t('checkout.zip_code'), 'zipCode', 'text', false)}
                    </div>

                    {/* Condición ante IVA (solo Argentina) */}
                    {this.isArgentina() && (
                        <div className="form-row" style={{ marginBottom: '0.75rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                                {t('checkout.tax_condition')} <span style={{ color: '#DF0D00' }}>*</span>
                            </label>
                            <select
                                className="form-control"
                                value={this.state.taxCondition}
                                onChange={(e) => this.setState({ taxCondition: e.target.value })}
                                style={this.isFieldInvalid('taxCondition') ? { borderColor: '#DF0D00', boxShadow: '0 0 0 2px rgba(223,13,0,0.15)' } : undefined}
                            >
                                <option value="">{t('checkout.select_tax_condition')}</option>
                                {TAX_CONDITIONS_AR.map(tc => (
                                    <option key={tc.value} value={tc.value}>{t(tc.labelKey)}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Método de pago */}
                    {billingCountry && (
                        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                            <h6 style={{ fontWeight: 700, marginBottom: '0.75rem' }}>{t('checkout.payment_method')}</h6>
                            <div style={{ display: 'grid', gridTemplateColumns: this.isArgentina() ? '1fr 1fr' : '1fr', gap: '0.6rem' }}>
                                {this.isArgentina() && this.renderPaymentOption(
                                    'mercadopago_ar', t('checkout.mp_option'), t('checkout.mp_description')
                                )}

                                {this.renderPaymentOption(
                                    'paypal', t('checkout.paypal_option'), t('checkout.paypal_description')
                                )}

                                {isAnnual && this.renderPaymentOption(
                                    'bank_transfer',
                                    this.isArgentina() ? t('checkout.transfer_option_ars') : t('checkout.transfer_option_usd'),
                                    t('checkout.transfer_description'),
                                    true
                                )}
                            </div>
                        </div>
                    )}

                    {/* Resumen de precio */}
                    {priceDetails && (
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            marginTop: '1rem',
                            border: '1px solid #dee2e6'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', marginBottom: '0.75rem' }}>
                                <h6 style={{ fontWeight: 700, margin: 0 }}>{t('checkout.price_summary')}</h6>
                                {priceDetails.discount > 0 && (
                                    <span style={{
                                        backgroundColor: '#259D92',
                                        color: '#fff',
                                        padding: '0.15em 0.55em',
                                        borderRadius: '1em',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.03em'
                                    }}>
                                        {t('checkout.discount_off', { discount: priceDetails.discount })}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                                <span>{t(plan.nameKey)}</span>
                                <span>{priceDetails.currencySymbol} {formatPrice(priceDetails.planPrice, priceDetails.currency)}</span>
                            </div>
                            {priceDetails.isArs && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', borderTop: '1px solid #dee2e6', paddingTop: '0.3rem' }}>
                                        <span>{t('checkout.subtotal')}</span>
                                        <span>{priceDetails.currencySymbol} {formatPrice(priceDetails.subtotal, priceDetails.currency)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                                        <span>{t('checkout.iva_21')}</span>
                                        <span>{priceDetails.currencySymbol} {formatPrice(priceDetails.iva, priceDetails.currency)}</span>
                                    </div>
                                </>
                            )}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                borderTop: '2px solid #dee2e6',
                                paddingTop: '0.5rem',
                                marginTop: '0.3rem'
                            }}>
                                <span>Total/{t('agency_subscription.month_short')}</span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5em' }}>
                                    {priceDetails.discount > 0 && (
                                        <span style={{
                                            textDecoration: 'line-through',
                                            color: '#B6B6B7',
                                            fontWeight: 400,
                                            fontSize: '0.85rem'
                                        }}>
                                            {priceDetails.currencySymbol} {formatPrice(priceDetails.monthlyTotal, priceDetails.currency)}
                                        </span>
                                    )}
                                    <span>{priceDetails.currencySymbol} {formatPrice(priceDetails.total, priceDetails.currency)}</span>
                                </div>
                            </div>

                            {/* Pago por período y ahorro — solo para períodos no mensuales */}
                            {priceDetails.discount > 0 && (
                                <div style={{
                                    backgroundColor: '#eef7f6',
                                    borderRadius: '0.4rem',
                                    padding: '0.7rem 0.85rem',
                                    marginTop: '0.65rem',
                                    border: '1px solid #c8e6e3'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '0.25rem'
                                    }}>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                                            {t('checkout.period_payment', { period: t(PERIOD_LABEL_KEYS[checkoutData.period]).toLowerCase() })}
                                        </span>
                                        <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#191F29' }}>
                                            {priceDetails.currencySymbol} {formatPrice(priceDetails.periodTotal, priceDetails.currency)}
                                        </span>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <span style={{ fontSize: '0.78rem', color: '#6c757d' }}>
                                            {t(`checkout.period_payment_note_${checkoutData.period}`)}
                                        </span>
                                        <span style={{ fontSize: '0.78rem', color: '#259D92', fontWeight: 600 }}>
                                            {t('checkout.you_save', {
                                                amount: `${priceDetails.currencySymbol} ${formatPrice(priceDetails.savings, priceDetails.currency)}`
                                            })}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comentario opcional */}
                    {priceDetails && (
                        <div style={{ marginTop: '1rem' }}>
                            <div
                                onClick={() => this.setState({ showComment: !this.state.showComment })}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4em',
                                    cursor: 'pointer',
                                    fontSize: '0.88rem',
                                    color: '#4A4A4A',
                                    userSelect: 'none',
                                }}
                            >
                                <i className={`fa fa-chevron-${this.state.showComment ? 'down' : 'right'}`}
                                   style={{ fontSize: '0.7em', width: '0.9em' }} />
                                <span>{t('checkout.comment_prompt')}</span>
                            </div>
                            {this.state.showComment && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <textarea
                                        className="form-control"
                                        rows={5}
                                        maxLength={500}
                                        placeholder={t('checkout.comment_placeholder')}
                                        value={this.state.comment}
                                        onChange={(e) => this.setState({ comment: e.target.value })}
                                        style={{ fontSize: '0.88rem', resize: 'vertical' }}
                                    />
                                    <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#B6B6B7', marginTop: '0.25rem' }}>
                                        {this.state.comment.length}/500
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div style={{
                            backgroundColor: '#f8d7da',
                            color: '#842029',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            marginTop: '1rem',
                            fontSize: '0.9rem'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Nota de procesamiento */}
                    <div style={{
                        fontSize: '0.8rem',
                        color: '#B6B6B7',
                        marginTop: '1rem',
                        textAlign: 'center',
                    }}>
                        {t('checkout.processing_note')}
                    </div>

                    {/* Validación */}
                    {this.state.showValidation && !this.isFormValid() && (
                        <div style={{
                            backgroundColor: '#fff3cd',
                            color: '#664d03',
                            padding: '0.65rem 0.85rem',
                            borderRadius: '0.4rem',
                            marginTop: '0.75rem',
                            fontSize: '0.85rem',
                            border: '1px solid #ffecb5'
                        }}>
                            {t('checkout.complete_required_fields')}
                        </div>
                    )}

                    {/* Botones */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '1rem',
                        gap: '1rem'
                    }}>
                        <Button color="secondary" onClick={onBack}>
                            {t('checkout.back')}
                        </Button>
                        {sending ? (
                            <Spinner color="positive small" />
                        ) : (
                            <Button
                                color="primary"
                                onClick={() => this.handleConfirmClick()}
                                style={{ minWidth: '200px' }}
                            >
                                {paymentMethod === 'bank_transfer'
                                    ? t('checkout.confirm_bank_transfer')
                                    : t('checkout.confirm_subscription')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation()(CheckoutForm);
